import { format as formatDate, isValid } from "date-fns";

/**
 * The Date the picker should show for a stored Formik value, or null.
 * Never throws: an Invalid Date, a non-date string, an empty value and the
 * placeholder dates 9999-12-31 / 1753-01-01 all give null.
 */
export function toPickerDate(raw: unknown): Date | null {
  if (!raw || raw === "9999-12-31" || raw === "1753-01-01") return null;
  const stored = new Date(raw as string | number | Date);
  if (!isValid(stored)) return null;
  return new Date(formatDate(stored, "yyyy-MM-dd"));
}

/**
 * True for a date the user has finished typing. rsuite 6 calls onChange on
 * every keystroke: with an Invalid Date while a field is empty, and with valid
 * but unfinished dates while the year is typed (0001, 0019, 0198 on the way to
 * 1980). Years below 1000 therefore count as unfinished; AIKA has no dates
 * that old.
 */
export function isCompleteDate(date: Date | null | undefined): date is Date {
  return !!date && isValid(date) && date.getFullYear() >= 1000;
}

/**
 * True when rsuite's onChange was triggered by typing in the field, as
 * opposed to a click in the calendar or a paste. rsuite passes the triggering
 * event as onChange's second argument.
 */
export function isTypingEvent(
  event: { type?: string; key?: string } | null | undefined,
): boolean {
  return event?.type === "keydown";
}

/**
 * Splits what rsuite's DatePicker sends to onChange into the value to store
 * and a draft for the picker to keep showing. An unfinished date stays in the
 * picker as `draft`, so the input keeps the digits typed so far, and the
 * stored value is only ever a finished Date or null.
 */
export function splitPickerChange(date: Date | null): {
  draft: Date | null;
  value: Date | null;
} {
  if (!date) return { draft: null, value: null };
  if (!isCompleteDate(date)) return { draft: date, value: null };
  return { draft: null, value: date };
}
